/**
 * Ecosystem-wide "Escape closes the top-most modal" behaviour.
 *
 * The shared showModal() already handles Esc itself. Most modals across the
 * apps, however, are hand-rolled overlay <div>s (a `position:fixed; inset:0`
 * backdrop appended to <body>) that never gained Esc support. Rather than edit
 * each one, this installs a single global handler: on Escape we locate the
 * top-most full-viewport overlay and re-fire its OWN backdrop-click close path
 * — the near-universal `if (e.target === overlay) close()` pattern — so the
 * component tears itself down cleanly (editor teardown, scroll unlock, etc.)
 * instead of us ripping the node out and leaking its listeners.
 *
 * Layers that own their Escape handling (showModal's `.modal-overlay`, the
 * global-search overlay) or aren't dismissable modals (celebration fireworks,
 * the mobile-nav backdrop) are excluded. Crucially, if one of those excluded
 * layers is itself the top-most layer we bail entirely, so we never reach past
 * the visible top layer to close something stacked underneath it.
 *
 * Caveat by design: modals that deliberately do NOT close on backdrop click
 * won't gain Esc from this (usually intentional — e.g. forms guarding against
 * accidental dismissal). Those should adopt showModal() if Esc is wanted.
 */

// Classes whose elements manage their own Esc, or simply aren't modals.
const EXCLUDED_CLASSES = [
  'modal-overlay',          // showModal() — has its own Esc handler
  'global-search-overlay',  // global search — own Esc + close button
  'celebration-overlay',    // birthday / anniversary fireworks (auto-dismiss)
  'sidebar-overlay',        // mobile-nav backdrop — not a modal
];

let _installed = false;

/**
 * Install the global Escape-to-close handler. Idempotent and safe to call
 * from multiple entry points; only the first call registers the listener.
 */
export function initGlobalEscDismiss() {
  if (_installed || typeof document === 'undefined') return;
  _installed = true;
  // Bubble phase so any component-level keydown handlers (and showModal's
  // own) run first; we only act on Escapes nobody else has consumed.
  document.addEventListener('keydown', onKeydown);
}

function onKeydown(e) {
  if (e.key !== 'Escape' && e.keyCode !== 27) return;
  if (e.defaultPrevented) return;

  const top = topOverlay();
  if (!top) return;

  // The top-most layer owns its Esc (or isn't a modal) — let it handle
  // itself and do NOT reach past it to close a layer underneath.
  if (isExcluded(top)) return;

  // Re-fire the overlay's own backdrop-click close path.
  top.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

function isExcluded(el) {
  return EXCLUDED_CLASSES.some((cls) => el.classList.contains(cls));
}

function zIndexOf(el) {
  const z = parseInt(getComputedStyle(el).zIndex, 10);
  return Number.isNaN(z) ? 0 : z;
}

/**
 * The visible, fixed/absolute, viewport-covering overlay that is on top
 * (highest z-index, then latest in DOM order). Returns null when no such
 * layer is open — in which case Escape is a no-op for us.
 */
function topOverlay() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Candidate set: direct <body> children (where overlays are appended) plus
  // anything explicitly flagged as a dialog/overlay elsewhere in the tree.
  const set = new Set(document.body.children);
  document
    .querySelectorAll('.modal-overlay, [role="dialog"][aria-modal="true"]')
    .forEach((el) => set.add(el));

  const candidates = [];
  for (const el of set) {
    if (!(el instanceof HTMLElement)) continue;
    const cs = getComputedStyle(el);
    if (cs.position !== 'fixed' && cs.position !== 'absolute') continue;
    if (cs.display === 'none' || cs.visibility === 'hidden') continue;
    if (parseFloat(cs.opacity) === 0) continue;
    const r = el.getBoundingClientRect();
    // Must cover most of the viewport to count as a modal backdrop —
    // filters out floating panels, dropdowns, toasts, the AI widget, etc.
    if (r.width < vw * 0.85 || r.height < vh * 0.85) continue;
    candidates.push(el);
  }
  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const za = zIndexOf(a);
    const zb = zIndexOf(b);
    if (za !== zb) return zb - za;
    // Equal z-index: whichever comes later in the DOM is rendered on top.
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? 1 : -1;
  });
  return candidates[0];
}
